import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  EventEmitter,
  Output,
  OnDestroy
} from '@angular/core';
import * as d3 from 'd3';
import { Subject } from 'rxjs';
import { SimplifyAP as simplify } from 'simplify-ts';
import { AnnotationModel, ModelItemType } from './interfaces';
import { LogService } from '../../../../services/log.service';

@Component({
    selector: 'app-annotation',
    templateUrl: './annotation.component.html',
    styleUrls: ['./annotation.component.scss'],
    standalone: false
})
export class AnnotationComponent implements AfterViewInit, OnDestroy {
  @ViewChild('comment', { static: false }) comment: ElementRef;
  @ViewChild('svgtarget', { static: true }) svgTarget: ElementRef;
  @Input() inputObject = {
    size: [],
    annotations: {
      items: []
    },
    svg: null,
    backgroundImage: null,
    backgroundImageUrl: null
  };

  @Input() availableColors = [
    {
      background: 'rgb(255,255,255)',
      foreground: 'rgb(0,0,0)'
    },
    {
      background: 'rgb(184, 230, 255)',
      foreground: 'rgb(0,0,0)'
    },
    {
      background: 'rgb(164, 72, 255)',
      foreground: 'rgb(255,255,255)'
    },
    {
      background: 'rgb(255, 248, 72)',
      foreground: 'rgb(0,0,0)'
    },
    {
      background: 'rgb(80,190,63)',
      foreground: 'rgb(255,255,255)'
    },
    {
      background: 'rgb(255,83,72)',
      foreground: 'rgb(255,255,255)'
    }
  ];

  @Output() getOutput = new EventEmitter();
  @Input() undoTrigger: Subject<any>;

  outputObject = {
    size: [0, 0],
    annotations: {
      items: []
    },
    svg: null
  };

  generatedIds = [];
  uidLength = 20;

  imgSrc = '';

  g: any;

  imgWidth = 0;
  imgHeight = 0;

  svg: any;
  target: any;
  image: any;

  zoomD3: any;
  zoomStep = 0;
  maxZoomFactor = 200;
  zoomFactor = 100;

  isColorSubmenuActive = false;
  selectedColor = { background: 'rgb(184, 230, 255)', foreground: 'rgb(0,0,0)' };

  selectedAction = null;

  numberingIndex = 1;
  isModalVisible = false;

  selectedNumberingElement: any;
  selectedShapeElement: any;
  selectedShapeColor: any;

  elementsCounter = 0;
  actionsCounter = 0;

  isShapeSubmenuActive = false;
  activeShapeIcon = 'crop_din';

  selectedPath = null;
  x = 0;
  y = 0;
  drag: any;
  previousPathColor = null;

  isStrokeSubmenuActive = false;
  strokeWidth = 5;
  availableStrokes = [2, 5, 10, 15];

  constructor(private logService: LogService) {}

  discard() {
    this.outputObject.size = this.inputObject.size;
    this.outputObject.annotations = this.inputObject.annotations;
    this.outputObject.svg = this.inputObject.svg;
    this.getOutput.emit(this.outputObject);
  }

  ngOnDestroy() {
    const target = d3.select(this.svgTarget.nativeElement);
    target.html('');
  }

  calcImageSize(imageWidth, imageHeight, rectWidth, rectHeight) {
    // Calculer les rapports de hauteur et de largeur
    const heightRatio = imageHeight / rectHeight;
    const widthRatio = imageWidth / rectWidth;

    // Déterminer si l'image est plus haute ou plus large que le rectangle
    if (heightRatio > widthRatio) {
      // L'image est plus haute que large, redimensionner en fonction de la hauteur
      return {
        height: imageHeight / heightRatio,
        width: imageWidth / heightRatio
      };
    } else {
      // L'image est plus large que haute, redimensionner en fonction de la largeur
      return {
        height: imageHeight / widthRatio,
        width: imageWidth / widthRatio
      };
    }
  }

  ngAfterViewInit() {
    this.undoTrigger.subscribe(() => this.discard());

    this.target = d3.select(this.svgTarget.nativeElement);
    this.target.html('');

    this.svg = this.target.insert('svg');
    // this.g = this.svg.append('g').style('pointer-events', 'visibleStroke');
    this.g = this.svg.append('g');

    const i = new Image();

    i.onload = () => {
      const isize = this.calcImageSize(i.width, i.height, 1024, 1024);
      const r = i.width / i.height;
      this.imgWidth = isize.width;
      this.imgHeight = isize.height;

      this.svg.attr('preserveAspectRatio', 'xMinYMin meet').attr('viewBox', `0 0 ${this.imgWidth} ${this.imgHeight}`);
      if (this.inputObject.size == null || !(this.inputObject.size.length > 0)) {
        this.svg.attr('width', this.imgWidth + 'px').attr('height', this.imgHeight + 'px');
        // this.svg.attr('max-width', this.imgWidth + 'px').attr('max-height', this.imgHeight + 'px');
        this.target
          .select('g')
          .attr('width', this.imgWidth + 'px')
          .attr('height', this.imgHeight + 'px');
        this.target
          .select('image')
          .attr('width', this.imgWidth + 'px')
          .attr('height', this.imgHeight + 'px');
      }
    };

    i.src = this.inputObject.backgroundImage;

    this.image = this.g
      .append('svg:image')
      .attr('image-rendering', 'optimizeQuality')
      .attr('xlink:href', this.inputObject.backgroundImage)
      .attr('width', '1280')
      .attr('height', '800')
      .attr('x', 0)
      .attr('y', 0);

    if (this.inputObject?.size !== null && this.inputObject?.size?.length > 0) {
      this.svg.attr('width', this.inputObject.size[0] + 'px').attr('height', this.inputObject.size[1] + 'px');
      this.svg.attr('max-width', this.inputObject.size[0] + 'px').attr('max-height', this.inputObject.size[1] + 'px');
      this.target
        .select('g')
        .attr('width', this.inputObject.size[0] + 'px')
        .attr('height', this.inputObject.size[1] + 'px');
      this.target
        .select('image')
        .attr('width', this.inputObject.size[0] + 'px')
        .attr('height', this.inputObject.size[1] + 'px');
    }

    this.drawInputObjects();
  }

  onListItemClick(item: string) {
    switch (item) {
      case 'zoomIn':
        this.removeEventsOnPreviousElements();
        this.closeMenus();
        this.zoom('in');
        break;
      case 'zoomOut':
        this.removeEventsOnPreviousElements();
        this.closeMenus();
        this.zoom('out');
        break;
      case 'numbering':
        this.removeEventsOnPreviousElements();
        this.closeMenus();
        this.numbering();
        break;
      case 'freeHand':
        this.removeEventsOnPreviousElements();
        this.closeMenus();
        this.freeHand();
        break;
      case 'shape':
        this.removeEventsOnPreviousElements();
        this.isColorSubmenuActive = false;
        this.isStrokeSubmenuActive = false;
        this.shape();
        break;
      case 'stroke':
        this.isColorSubmenuActive = false;
        this.isShapeSubmenuActive = false;
        this.stroke();
        break;
      case 'color':
        this.isShapeSubmenuActive = false;
        this.isStrokeSubmenuActive = false;
        this.color();
        break;
      default:
        break;
    }
  }

  /**
   * ZOOM FUNCTIONALITY
   *
   */

  zoom(direction: 'in' | 'out') {
    this.g.on('click', null);
    this.target.select('svg').style('cursor', 'default');

    if (this.selectedAction !== 'zoom-' + direction) {
      this.selectedAction = 'zoom-' + direction;
      this.target.select('svg').style('cursor', 'zoom-' + direction);

      this.target.select('svg').on('dblclick.zoom', null);

      this.target.select('svg').on('click', (e) => {
        e.preventDefault();
        direction === 'in' ? (this.zoomFactor += 10) : (this.zoomFactor -= 10);
        this.zoomFactor = Math.max(100, Math.min(this.zoomFactor, this.maxZoomFactor));
        this.target.select('svg').style('transform', `scale(${this.zoomFactor}%)`);
      });
    } else {
      this.selectedAction = null;
      this.target.select('svg').style('cursor', 'default').on('.zoom', null).on('click', null);
      this.target.select('g').on('click', null);
    }
  }

  zoomed(e: any) {
    //  this.target.select('svg g').attr('transform', e.transform);
  }

  /**
   * NUMBERING FUNCTIONALITY
   *
   */

  numbering() {
    this.target.select('svg').style('cursor', 'default').on('.zoom', null).on('click', null);

    if (this.selectedAction !== 'numbering') {
      this.selectedAction = 'numbering';

      this.target.select('svg').style('cursor', 'crosshair');

      this.g.on('click', (e: any) => {
        const coordinates = d3.pointer(event);

        const newG = this.target
          .select('g')
          .append('g')
          .attr('id', this.numberingIndex)
          .attr('uid', this.generateUID())
          .attr('x', coordinates[0])
          .attr('y', coordinates[1]);

        newG
          .append('circle')
          .attr('r', '3%')
          .attr('fill', this.selectedColor.background)
          .attr('cx', coordinates[0])
          .attr('cy', coordinates[1])
          .attr('id', 'c' + this.numberingIndex);

        newG
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('alignment-baseline', 'middle')
          .attr('x', coordinates[0])
          .attr('y', coordinates[1])
          .attr('fill-opacity', 1)
          .attr('fill', this.selectedColor.foreground)
          .attr('id', 't' + this.numberingIndex)
          .text(this.numberingIndex.toString())
          .style('user-select', 'none')
          .style('font-size', '25px')
          .style('line-height', '0.9');

        this.saveNumbering(newG);

        newG
          .on('click', (event: any) => {
            if (!this.selectedAction || ['description', 'color', 'stroke'].indexOf(this.selectedAction) !== -1) {
              this.doActionsNumbering(newG);
            }
          })
          .on('touchend', () => {
            if (!this.selectedAction || ['description', 'color', 'stroke'].indexOf(this.selectedAction) !== -1) {
              this.doActionsNumbering(newG);
            }
          })
          .on('dblclick', () => {
            this.description();
          });

        this.g.on('click', null);
        this.target.select('svg').style('cursor', 'default');
        this.selectedAction = null;

        this.doActionsNumbering(newG);
        this.description();

        this.elementsCounter++;
        this.numberingIndex++;
        this.actionsCounter++;
      });
    } else {
      this.g.on('click', null);
      this.target.select('svg').style('cursor', 'default');
      this.selectedAction = null;
    }
  }

  doActionsNumbering(g: any) {
    this.removeEventsOnPreviousElements();

    this.selectedNumberingElement = g;
    const id = g.attr('id');

    this.target.select('.numbering-selected').classed('numbering-selected', false);
    this.target.select('#c' + id).classed('numbering-selected', true);

    this.target.select('body').on('keyup', (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const modal = d3.select('.photoeditor-modal');
        if (!modal.classed('show-modal')) {
          this.deleteShape();
        }
      }
    });

    // move
    if (this.selectedAction === null || this.selectedAction === 'description') {
      this.moveNumbering(g);
    }

    this.g.on('click', (e) => {
      const gId = g.attr('id');

      if (e.target !== this.target.select('#c' + gId).node() && e.target !== this.target.select('#t' + gId).node()) {
        if (!this.isModalVisible) {
          this.description();
        }
        this.svg.style('cursor', 'default');
        this.g.on('click', null);
        this.removeNumberingEventsOnPreviousElement();
      }
    });
  }

  getTranslation(transform) {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttributeNS(null, 'transform', transform);
    const matrix = g.transform.baseVal.consolidate().matrix;
    return [matrix.e, matrix.f];
  }

  moveNumbering(g: any) {
    g.style('cursor', 'all-scroll');
    g.call(
      d3
        .drag()
        .on('drag', (d) => {
          this.x += d.dx;
          this.y += d.dy;

          g.attr('transform', 'translate(' + this.x + ',' + this.y + ')');
        })
        .on('start', (e) => {
          this.target.select('svg').style('cursor', 'all-scroll');
        })
        .on('end', (e) => {
          if (g.attr('transform')) {
            const translation = this.getTranslation(g.attr('transform'));
            g.attr('x', +g.attr('x') + translation[0]).attr('y', +g.attr('y') + translation[1]);

            const previousElementId = this.selectedNumberingElement.attr('id');
            const circle = this.target.select('#c' + previousElementId);
            circle.attr('cx', +circle.attr('cx') + translation[0]).attr('cy', +circle.attr('cy') + translation[1]);

            const text = this.target.select('#t' + previousElementId);
            text.attr('x', +text.attr('x') + translation[0]).attr('y', +text.attr('y') + translation[1]);

            this.x = 0;
            this.y = 0;

            g.attr('transform', null);
            this.saveNumbering(g);
          }
        })
    );
  }

  removeNumberingEventsOnPreviousElement(isDelete?) {
    if (this.selectedNumberingElement) {
      if (!isDelete) {
        this.saveNumbering(this.selectedNumberingElement);
      }

      this.selectedNumberingElement.on('.drag', null);
      this.selectedNumberingElement.style('cursor', 'default').on('mousedown', null);
      this.svg.on('mousemove', null).on('mouseup', null);

      const previousElementId = this.selectedNumberingElement.attr('id');

      this.target.select('.numbering-selected').classed('numbering-selected', false);

      this.selectedNumberingElement = null;
    } else {
      this.g.on('click', null);
    }
  }

  description() {
    const modal = d3.select('.photoeditor-modal');
    const trigger = this.target.select('#trigger');
    const saveModalButton = modal.select('.save-modal-button');
    const closeModalButton = modal.select('.close-modal-button');
    const closeButton = modal.select('.close-button');

    this.toggleModal(modal);
    closeButton.on('click', () => {
      this.closeModal(modal);
    });
    closeModalButton.on('click', () => {
      this.closeModal(modal);
    });
    saveModalButton.on('click', () => {
      this.toggleModal(modal);
    });
  }

  closeModal(modal: any) {
    modal.classed('show-modal', !modal.classed('show-modal'));
    this.isModalVisible = !modal.classed('show-modal');
  }

  toggleModal(modal: any) {
    if (modal.classed('show-modal')) {
      if (this.selectedNumberingElement) {
        const comm = this.comment.nativeElement.value;
        this.selectedNumberingElement.attr('title', comm);
        this.saveNumbering(this.selectedNumberingElement);
      }
    } else {
      this.comment.nativeElement.value = this.selectedNumberingElement.attr('title');
    }

    modal.classed('show-modal', !modal.classed('show-modal'));
    this.isModalVisible = !modal.classed('show-modal');
  }

  reNumber() {
    const numbers = this.outputObject.annotations.items.filter((item) => item.type === ModelItemType.number);

    if (numbers.length === 0) {
      this.numberingIndex = 1;
      return true;
    }

    if (numbers.length === 1) {
      const g = this.target.select('g[id="' + numbers[0].data.number + '"]');
      g.attr('id', 1);
      this.target.select('#c' + numbers[0].data.number).attr('id', 'c1');
      this.target
        .select('#t' + numbers[0].data.number)
        .attr('id', 't1')
        .text('1');

      numbers[0].data.number = 1;
      this.numberingIndex = 2;

      return true;
    }

    numbers.map((item, index) => {
      const nextItem = this.outputObject.annotations.items[index + 1];

      if (nextItem) {
        if (nextItem.data.number > item.data.number + 1) {
          const g = this.target.select('g[id="' + nextItem.data.number + '"]');
          g.attr('id', item.data.number + 1);
          this.target.select('#c' + nextItem.data.number).attr('id', 'c' + (item.data.number + 1));
          this.target
            .select('#t' + nextItem.data.number)
            .attr('id', 't' + (item.data.number + 1))
            .text((item.data.number + 1).toString());

          nextItem.data.number = item.data.number + 1;
          this.numberingIndex = nextItem.data.number + 1;
        }
      } else {
        if (this.numberingIndex > item.data.number + 1) {
          this.numberingIndex -= 1;
        }
      }
    });

    if (numbers[0].data.number !== 1) {
      const g = this.target.select('g[id="' + numbers[0].data.number + '"]');
      g.attr('id', 1);
      this.target.select('#c' + numbers[0].data.number).attr('id', 'c1');
      this.target
        .select('#t' + numbers[0].data.number)
        .attr('id', 't1')
        .text('1');

      numbers[0].data.number = 1;
      this.reNumber();
    }
  }

  /**
   * FREE HAND FUNCTIONALITY
   *
   */

  freeHand() {
    this.g.on('click', null);
    this.target.select('svg').style('cursor', 'default');

    if (this.selectedAction !== 'freeHand') {
      this.selectedAction = 'freeHand';

      this.x = 0;
      this.y = 0;

      let ptData = [];
      let path;
      let drawing = false;

      const line = d3
        .line()
        .curve(d3.curveCardinal)
        .x((d) => d[0])
        .y((d) => d[1]);

      const listen = () => {
        if (this.selectedAction === 'freeHand') {
          drawing = true;
          ptData = []; // reset point data
          path = this.g
            .append('path') // start a new line
            .data([ptData])
            .attr('stroke', this.selectedColor.background)
            .attr('uid', this.generateUID())
            .attr('fill', 'none')
            .attr('stroke-width', this.strokeWidth)
            .attr('stroke-linejoin', 'round')
            .attr('stroke-linecap', 'round')
            .attr('d', line)
            .on('click', () => {
              if (this.selectedAction === null) {
                this.selectLine(path);
              }
            });

          this.g.on('mousemove', onmove);
        }
      };

      const ignore = () => {
        let before;
        let after;
        this.g.on('mousemove', null);
        this.g.on('touchmove', null);

        // skip out if we're not drawing
        if (!drawing) {
          return;
        }
        drawing = false;

        before = ptData.length;

        // simplify
        ptData = simplify(ptData);
        after = ptData.length;

        // redraw the line after simplification
        path.attr('d', line(ptData));

        this.savePath(path);
        this.selectLine(path);

        this.elementsCounter++;
      };

      const onmove = (e: any) => {
        let point: any;

        point = d3.pointer(event);

        // push a new data point onto the back
        ptData.push(point);
        path.attr('d', line(ptData));
      };

      this.g
        .on('mousedown', listen)
        .on('touchstart', listen)
        .on('touchend', ignore)
        .on('touchleave', ignore)
        .on('mouseup', ignore)
        .on('mouseleave', ignore);
    } else {
      this.g
        .on('mousedown', null)
        .on('mousemove', null)
        .on('touchstart', null)
        .on('touchend', null)
        .on('touchmove', null)
        .on('touchleave', null)
        .on('mouseup', null)
        .on('mouseleave', null);

      this.selectedAction = null;
    }
  }

  selectLine(path: any) {
    this.selectedPath = path;

    this.target.select('body').on('keyup', (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        this.image.on('click', null);
        this.deleteShape();
      }
    });

    this.g
      .on('mousedown', null)
      .on('mousemove', null)
      .on('touchstart', null)
      .on('touchend', null)
      .on('touchmove', null)
      .on('touchleave', null)
      .on('mouseup', null)
      .on('mouseleave', null);

    this.selectedAction = null;

    this.previousPathColor = path.attr('stroke');
    path.classed('selected', true).style('cursor', 'all-scroll;');

    this.drag = d3
      .drag()
      .on('drag', (d) => {
        if (this.selectedPath) {
          this.x += d.dx;
          this.y += d.dy;

          path.attr('transform', 'translate(' + this.x + ',' + this.y + ')');
        }
      })
      .on('start', (e) => {
        if (this.selectedPath) {
          this.target.select('svg').style('cursor', 'all-scroll');
        }
      })
      .on('end', () => {
        this.savePath(path);
      });

    path.call(this.drag);

    this.image.on('click', (e) => {
      if (e.target !== path.node()) {
        this.savePath(path);
        this.removePreviousPathEvents();

        this.g.style('cursor', 'default');
        this.g.on('click', null);
      }
    });
  }

  removePreviousPathEvents(isDelete?) {
    if (this.selectedPath) {
      this.selectedPath
        .attr('stroke', this.previousPathColor)
        .style('cursor', 'default; outline: none')
        .classed('selected', false);

      if (!isDelete) {
        this.savePath(this.selectedPath);
      }
    }

    this.image.on('click', null);
    this.drag = null;
    this.selectedPath.on('.drag', null);
    this.selectedPath = null;
    this.target.select('svg').style('cursor', 'default');
  }

  /**
   * SHAPE FUNCTIONALITY
   *
   */

  shape() {
    this.isShapeSubmenuActive = !this.isShapeSubmenuActive;
    this.target.select('svg').on('.zoom', null).on('click', null);

    if (this.isShapeSubmenuActive) {
      this.selectedAction = 'shape';
      this.target.select('svg').style('cursor', 'default');
    }
  }

  rectangleShape() {
    this.activeShapeIcon = 'crop_din';

    if (this.selectedAction !== 'rectangle') {
      this.selectedAction = 'rectangle';

      this.target.select('svg').style('cursor', 'crosshair');

      this.g.on('click', (e: any) => {
        const coordinates = d3.pointer(event);

        const newG = this.target
          .select('g')
          .append('g')
          .attr('x', coordinates[0])
          .attr('y', coordinates[1])
          .attr('id', 'g-' + this.actionsCounter)
          .attr('uid', this.generateUID());

        const rectangle = newG
          .append('rect')
          .attr('stroke', this.selectedColor.background)
          .style('stroke-width', this.strokeWidth)
          .attr('width', 50)
          .attr('height', 50)
          .attr('fill', 'none')
          .attr('id', 'rect' + this.actionsCounter);

        rectangle
          .attr('x', coordinates[0] - parseFloat(rectangle.attr('width')) / 2)
          .attr('y', coordinates[1] - parseFloat(rectangle.attr('height')) / 2);

        newG
          .on('click', (event: any) => {
            if (!this.selectedAction || ['color', 'stroke'].indexOf(this.selectedAction) !== -1) {
              this.doActionsRectangle(newG, rectangle);
            }
          })
          .on('touchend', () => {
            if (!this.selectedAction || ['color', 'stroke'].indexOf(this.selectedAction) !== -1) {
              this.doActionsRectangle(newG, rectangle);
            }
          });
        this.g.on('click', null);
        this.target.select('svg').style('cursor', 'default');
        this.selectedAction = null;

        this.saveRectangle(newG);

        this.doActionsRectangle(newG, rectangle);

        this.elementsCounter++;
        this.actionsCounter++;
      });
    } else {
      this.g.on('click', null);
      this.target.select('svg').style('cursor', 'default');
      this.selectedAction = null;
    }
  }

  doActionsRectangle(g: any, rectangle: any) {
    this.removeEventsOnPreviousElements();

    this.selectedShapeElement = g;
    const id = g.attr('id');

    this.selectedShapeColor = this.target.select('#rect' + id.toString().split('-')[1]).attr('stroke');
    this.target.select('#rect' + id.toString().split('-')[1]).classed('selected', true);

    this.target.select('body').on('keyup', (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        this.g.on('click', null);
        this.deleteShape();
      }
    });

    // resize handles
    const cTopLeft = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3c9ed4')
      .attr('id', 'cTopLeft')
      .attr('cx', parseFloat(rectangle.attr('x')))
      .attr('cy', parseFloat(rectangle.attr('y')))
      .style('cursor', 'nw-resize')
      .classed('shadow-svg', true);

    const cTopRight = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3c9ed4')
      .attr('id', 'cTopRight')
      .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
      .attr('cy', parseFloat(rectangle.attr('y')))
      .style('cursor', 'ne-resize')
      .classed('shadow-svg', true);

    const cBottomRight = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3c9ed4')
      .attr('id', 'cBottomRight')
      .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
      .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')))
      .style('cursor', 'se-resize')
      .classed('shadow-svg', true);

    const cBottomLeft = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3c9ed4')
      .attr('id', 'cBottomLeft')
      .attr('cx', parseFloat(rectangle.attr('x')))
      .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')))
      .style('cursor', 'sw-resize')
      .classed('shadow-svg', true);

    cTopLeft.raise();
    cTopRight.raise();
    cBottomRight.raise();
    cBottomLeft.raise();

    // move & resize
    if (this.selectedAction === null) {
      this.moveRectangle(g, rectangle, { tl: cTopLeft, tr: cTopRight, bl: cBottomLeft, br: cBottomRight });
    }

    this.g.on('click', (e) => {
      const gId = g.attr('id');

      if (
        e.target !== this.target.select('#rect' + gId.split('-')[1]).node() &&
        ![cTopLeft.node(), cTopRight.node(), cBottomRight.node(), cBottomLeft.node()].includes(e.target)
      ) {
        this.removeShapeEventsOnPreviousElement();
        this.g.on('click', null);
        this.saveRectangle(g);
      }
    });
  }

  moveRectangle(g: any, rectangle: any, resizeHandles: any) {
    let isDown = false;

    let initialPosX;
    let initialPosY;

    let isTl = false;
    let isTr = false;
    let isBl = false;
    let isBr = false;

    const x = 0;
    const y = 0;

    g.style('cursor', 'all-scroll')
      .on('mousedown', (e) => {
        isDown = true;
      })
      .on('touchstart', () => {
        isDown = true;
      });

    let isResizeDown = false;
    // add event listeners on resize handles
    resizeHandles.tl
      .on('mousedown', (e) => {
        isResizeDown = true;
        isTl = true;
        isBl = false;
        isBr = false;
        isTr = false;
        initialPosX = parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width'));
        initialPosY = parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height'));
      })
      .on('touchstart', () => {
        isResizeDown = true;
        isTl = true;
        isBl = false;
        isBr = false;
        isTr = false;
        initialPosX = parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width'));
        initialPosY = parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height'));
      });

    resizeHandles.tr
      .on('mousedown', () => {
        isResizeDown = true;
        isTr = true;
        isBl = false;
        isBr = false;
        isTl = false;
        initialPosX = parseFloat(rectangle.attr('x'));
        initialPosY = parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height'));
      })
      .on('touchstart', () => {
        isResizeDown = true;
        isTr = true;
        isBl = false;
        isBr = false;
        isTl = false;
        initialPosX = parseFloat(rectangle.attr('x'));
        initialPosY = parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height'));
      });

    resizeHandles.br
      .on('mousedown', () => {
        isResizeDown = true;
        isBr = true;
        isBl = false;
        isTl = false;
        isTr = false;
        initialPosX = parseFloat(rectangle.attr('x'));
        initialPosY = parseFloat(rectangle.attr('y'));
      })
      .on('touchstart', () => {
        isResizeDown = true;
        isBr = true;
        isBl = false;
        isTl = false;
        isTr = false;
        initialPosX = parseFloat(rectangle.attr('x'));
        initialPosY = parseFloat(rectangle.attr('y'));
      });

    resizeHandles.bl
      .on('mousedown', () => {
        isResizeDown = true;
        isBl = true;
        isBr = false;
        isTl = false;
        isTr = false;
        initialPosX = parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width'));
        initialPosY = parseFloat(rectangle.attr('y'));
      })
      .on('touchstart', () => {
        isResizeDown = true;
        isBl = true;
        isBr = false;
        isTl = false;
        isTr = false;
        initialPosX = parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width'));
        initialPosY = parseFloat(rectangle.attr('y'));
      });

    // g.call(
    //   d3.drag()
    //     .on('drag', (e) => {
    //       x += e.dx;
    //       y += e.dy;

    //       g.attr('x', x);
    //       g.attr('y', y);
    //       rectangle.attr('x', x);
    //       rectangle.attr('y', y);

    //       resizeHandles.tl.attr('cx', parseFloat(rectangle.attr('x'))).attr('cy', parseFloat(rectangle.attr('y')));
    //       resizeHandles.tr
    //         .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
    //         .attr('cy', parseFloat(rectangle.attr('y')));
    //       resizeHandles.bl
    //         .attr('cx', parseFloat(rectangle.attr('x')))
    //         .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));
    //       resizeHandles.br
    //         .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
    //         .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));
    //     })
    // );

    this.svg
      .on('mousemove', (e) => {
        if (isDown) {
          g.each((d: any) => {
            g.attr('x', parseFloat(g.attr('x')) + e.movementX);
            g.attr('y', parseFloat(g.attr('y')) + e.movementY);
            rectangle.attr('x', parseFloat(rectangle.attr('x')) + e.movementX);
            rectangle.attr('y', parseFloat(rectangle.attr('y')) + e.movementY);

            resizeHandles.tl.attr('cx', parseFloat(rectangle.attr('x'))).attr('cy', parseFloat(rectangle.attr('y')));
            resizeHandles.tr
              .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
              .attr('cy', parseFloat(rectangle.attr('y')));
            resizeHandles.bl
              .attr('cx', parseFloat(rectangle.attr('x')))
              .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));
            resizeHandles.br
              .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
              .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));
          });
        }

        if (isResizeDown) {
          const coordinates = d3.pointer(event);
          if (isTl) {
            resizeHandles.tl.attr('cx', coordinates[0]).attr('cy', coordinates[1]);

            resizeHandles.tr.attr('cy', coordinates[1]);

            resizeHandles.bl.attr('cx', coordinates[0]);
          }

          if (isTr) {
            resizeHandles.tr.attr('cx', coordinates[0]).attr('cy', coordinates[1]);

            resizeHandles.tl.attr('cy', coordinates[1]);

            resizeHandles.br.attr('cx', coordinates[0]);
          }

          if (isBr) {
            resizeHandles.br.attr('cx', coordinates[0]).attr('cy', coordinates[1]);

            resizeHandles.bl.attr('cy', coordinates[1]);

            resizeHandles.tr.attr('cx', coordinates[0]);
          }

          if (isBl) {
            resizeHandles.bl.attr('cx', coordinates[0]).attr('cy', coordinates[1]);

            resizeHandles.br.attr('cy', coordinates[1]);

            resizeHandles.tl.attr('cx', coordinates[0]);
          }

          rectangle
            .attr('x', Math.min(initialPosX, coordinates[0]))
            .attr('y', Math.min(initialPosY, coordinates[1]))
            .attr('width', Math.abs(initialPosX - coordinates[0]))
            .attr('height', Math.abs(initialPosY - coordinates[1]));
        }
      })
      .on('mouseup', () => {
        isDown = false;
        isResizeDown = false;

        // reposition resize handles
        resizeHandles.tl.attr('cx', parseFloat(rectangle.attr('x'))).attr('cy', parseFloat(rectangle.attr('y')));

        resizeHandles.tr
          .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
          .attr('cy', parseFloat(rectangle.attr('y')));

        resizeHandles.br
          .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
          .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));

        resizeHandles.bl
          .attr('cx', parseFloat(rectangle.attr('x')))
          .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));

        this.target.select('svg').style('cursor', 'default');
        this.saveRectangle(g);
      })
      .on('touchmove', (e: any) => {
        if (isDown) {
          g.each((d: any) => {
            g.attr('x', e.touches[0].clientX - 60);
            g.attr('y', e.touches[0].clientY - 60);
            rectangle.attr('x', e.touches[0].clientX - parseFloat(rectangle.attr('width')) / 2 - 30);
            rectangle.attr('y', e.touches[0].clientY - parseFloat(rectangle.attr('height')) / 2 - 60);
          });
        }

        if (isResizeDown) {
          const coordinates = d3.pointer(event);
          if (isTl) {
            resizeHandles.tl.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.tr.attr('cy', e.touches[0].clientY);

            resizeHandles.bl.attr('cx', e.touches[0].clientX);
          }

          if (isTr) {
            resizeHandles.tr.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.tl.attr('cy', e.touches[0].clientY);

            resizeHandles.br.attr('cx', e.touches[0].clientX);
          }

          if (isBr) {
            resizeHandles.br.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.bl.attr('cy', e.touches[0].clientY);

            resizeHandles.tr.attr('cx', e.touches[0].clientX);
          }

          if (isBl) {
            resizeHandles.bl.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.br.attr('cy', e.touches[0].clientY);

            resizeHandles.tl.attr('cx', e.touches[0].clientX);
          }

          rectangle
            .attr('x', Math.min(initialPosX, e.touches[0].clientX))
            .attr('y', Math.min(initialPosY, e.touches[0].clientY))
            .attr('width', Math.abs(initialPosX - e.touches[0].clientX))
            .attr('height', Math.abs(initialPosY - e.touches[0].clientY));
        }
      })
      .on('touchup', function () {
        isDown = false;
        isResizeDown = false;

        // reposition resize handles
        resizeHandles.tl.attr('cx', parseFloat(rectangle.attr('x'))).attr('cy', parseFloat(rectangle.attr('y')));

        resizeHandles.tr
          .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
          .attr('cy', parseFloat(rectangle.attr('y')));

        resizeHandles.br
          .attr('cx', parseFloat(rectangle.attr('x')) + parseFloat(rectangle.attr('width')))
          .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));

        resizeHandles.bl
          .attr('cx', parseFloat(rectangle.attr('x')))
          .attr('cy', parseFloat(rectangle.attr('y')) + parseFloat(rectangle.attr('height')));

        this.target.select('svg').style('cursor', 'default');
        this.saveRectangle(g);
      });
  }

  removeShapeEventsOnPreviousElement() {
    if (this.selectedShapeElement) {
      this.selectedShapeElement.style('cursor', 'default').on('mousedown', null);

      this.svg.on('mousemove', null).on('mouseup', null);

      const previousElementId = this.selectedShapeElement.attr('id').toString().split('-')[1];

      this.target
        .select('#rect' + previousElementId)
        .attr('stroke', this.selectedShapeColor || 'rgb(100, 100, 100)')
        .classed('selected', false);

      this.target
        .select('#circle-' + previousElementId)
        .attr('stroke', this.selectedShapeColor || 'rgb(100, 100, 100)')
        .classed('selected', false);

      this.target.select('#cTopLeft').remove();
      this.target.select('#cTopRight').remove();
      this.target.select('#cBottomRight').remove();
      this.target.select('#cBottomLeft').remove();

      this.selectedShapeElement = null;
    }
  }

  circleShape() {
    this.activeShapeIcon = 'circle';

    if (this.selectedAction !== 'circle') {
      this.selectedAction = 'circle';

      this.target.select('svg').style('cursor', 'crosshair');

      this.g.on('click', (e: any) => {
        const coordinates = d3.pointer(event);

        const newG = this.target
          .select('g')
          .append('g')
          .attr('id', 'g-' + this.actionsCounter)
          .attr('x', coordinates[0])
          .attr('y', coordinates[1])
          .attr('uid', this.generateUID());

        const circle = newG
          .append('circle')
          .attr('r', 30)
          .attr('stroke', this.selectedColor.background)
          .style('stroke-width', this.strokeWidth)
          .attr('fill', 'none')
          .attr('id', 'circle-' + this.actionsCounter)
          .attr('cx', coordinates[0])
          .attr('cy', coordinates[1]);

        newG
          .on('click', (event: any) => {
            if (!this.selectedAction) {
              this.doActionsCircle(newG, circle);
            }
          })
          .on('touchend', () => {
            if (!this.selectedAction) {
              this.doActionsCircle(newG, circle);
            }
          });

        this.saveCircle(newG);

        this.g.on('click', null);
        this.target.select('svg').style('cursor', 'default');
        this.selectedAction = null;

        this.doActionsCircle(newG, circle);

        this.elementsCounter++;
        this.actionsCounter++;
      });
    } else {
      this.g.on('click', null);
      this.target.select('svg').style('cursor', 'default');
      this.selectedAction = null;
    }
  }

  doActionsCircle(g: any, circle: any) {
    this.removeEventsOnPreviousElements();

    this.selectedShapeElement = g;
    const id = g.attr('id');

    this.selectedShapeColor = this.target.select('#circle-' + id.toString().split('-')[1]).attr('stroke');
    this.target.select('#circle-' + id.toString().split('-')[1]).classed('selected', true);

    this.target.select('body').on('keyup', (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        this.g.on('click', null);
        this.deleteShape();
      }
    });

    // resize handles
    const cTopLeft = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3C9ED4')
      .attr('id', 'cTopLeft')
      .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
      .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')))
      .style('cursor', 'nw-resize')
      .classed('shadow-svg', true);

    const cTopRight = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3C9ED4')
      .attr('id', 'cTopRight')
      .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
      .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')))
      .style('cursor', 'ne-resize')
      .classed('shadow-svg', true);

    const cBottomRight = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3C9ED4')
      .attr('id', 'cBottomRight')
      .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
      .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')))
      .style('cursor', 'se-resize')
      .classed('shadow-svg', true);

    const cBottomLeft = this.g
      .append('circle')
      .attr('r', '7')
      .attr('stroke', 'white')
      .style('stroke-width', 2)
      .attr('fill', '#3c9ed4')
      .attr('id', 'cBottomLeft')
      .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
      .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')))
      .style('cursor', 'sw-resize')
      .classed('shadow-svg', true);

    cTopLeft.raise();
    cTopRight.raise();
    cBottomRight.raise();
    cBottomLeft.raise();

    // move & resize
    if (this.selectedAction === null) {
      this.moveCircle(g, circle, { tl: cTopLeft, tr: cTopRight, bl: cBottomLeft, br: cBottomRight });
    }

    this.g.on('click', (e) => {
      const gId = g.attr('id');

      if (
        e.target !== this.target.select('#circle-' + gId.split('-')[1]).node() &&
        ![cTopLeft.node(), cTopRight.node(), cBottomRight.node(), cBottomLeft.node()].includes(e.target)
      ) {
        this.removeShapeEventsOnPreviousElement();
        this.g.on('click', null);
        this.saveCircle(g);
      }
    });
  }

  moveCircle(g: any, circle: any, resizeHandles: any) {
    let isDown = false;

    const initialPosX = parseFloat(circle.attr('cx'));

    const isTl = false;
    const isTr = false;
    const isBl = false;
    const isBr = false;

    g.style('cursor', 'all-scroll')
      .on('mousedown', () => {
        isDown = true;
      })
      .on('touchstart', () => {
        isDown = true;
      });

    let isResizeDown = false;
    // add event listeners on resize handles
    resizeHandles.tl
      .on('mousedown', () => {
        isResizeDown = true;
      })
      .on('touchstart', () => {
        isResizeDown = true;
      });

    resizeHandles.tr
      .on('mousedown', () => {
        isResizeDown = true;
      })
      .on('touchstart', () => {
        isResizeDown = true;
      });

    resizeHandles.br
      .on('mousedown', () => {
        isResizeDown = true;
      })
      .on('touchstart', () => {
        isResizeDown = true;
      });

    resizeHandles.bl
      .on('mousedown', () => {
        isResizeDown = true;
      })
      .on('touchstart', () => {
        isResizeDown = true;
      });

    this.svg
      .on('mousemove', (e) => {
        if (isDown) {
          g.each((d: any) => {
            g.attr('x', parseFloat(g.attr('x')) + e.movementX);
            g.attr('y', parseFloat(g.attr('y')) + e.movementY);
            circle.attr('cx', parseFloat(circle.attr('cx')) + e.movementX);
            circle.attr('cy', parseFloat(circle.attr('cy')) + e.movementY);

            resizeHandles.tl
              .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
              .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));
            resizeHandles.tr
              .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
              .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));
            resizeHandles.bl
              .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
              .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));
            resizeHandles.br
              .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
              .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));
          });
        }

        if (isResizeDown) {
          const coordinates = d3.pointer(event);

          resizeHandles.tl
            .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
            .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));

          resizeHandles.tr
            .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
            .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));

          resizeHandles.bl
            .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
            .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));

          resizeHandles.br
            .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
            .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));

          circle.attr('r', Math.abs(initialPosX - coordinates[0]));
        }
      })
      .on('mouseup', () => {
        isDown = false;
        isResizeDown = false;

        // reposition resize handles
        resizeHandles.tl
          .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));

        resizeHandles.tr
          .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));

        resizeHandles.bl
          .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));

        resizeHandles.br
          .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));

        this.target.select('svg').style('cursor', 'default');
        this.saveCircle(g);
      })
      .on('touchmove', (e: any) => {
        if (isDown) {
          g.each((d: any) => {
            g.attr('x', e.touches[0].clientX - 60);
            g.attr('y', e.touches[0].clientY - 60);
            circle.attr('cx', e.touches[0].clientX - 30);
            circle.attr('cy', e.touches[0].clientY - 60);
          });
        }

        if (isResizeDown) {
          if (isTl) {
            resizeHandles.tl.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.tr.attr('cy', e.touches[0].clientY);

            resizeHandles.bl.attr('cx', e.touches[0].clientX);
          }

          if (isTr) {
            resizeHandles.tr.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.tl.attr('cy', e.touches[0].clientY);

            resizeHandles.br.attr('cx', e.touches[0].clientX);
          }

          if (isBr) {
            resizeHandles.br.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.bl.attr('cy', e.touches[0].clientY);

            resizeHandles.tr.attr('cx', e.touches[0].clientX);
          }

          if (isBl) {
            resizeHandles.bl.attr('cx', e.touches[0].clientX).attr('cy', e.touches[0].clientY);

            resizeHandles.br.attr('cy', e.touches[0].clientY);

            resizeHandles.tl.attr('cx', e.touches[0].clientX);
          }

          circle.attr('r', Math.abs(initialPosX - e.touches[0].clientX));
        }
      })
      .on('touchup', function () {
        isDown = false;
        isResizeDown = false;

        // reposition resize handles
        resizeHandles.tl
          .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));

        resizeHandles.tr
          .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) - parseFloat(circle.attr('r')));

        resizeHandles.bl
          .attr('cx', parseFloat(circle.attr('cx')) - parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));

        resizeHandles.br
          .attr('cx', parseFloat(circle.attr('cx')) + parseFloat(circle.attr('r')))
          .attr('cy', parseFloat(circle.attr('cy')) + parseFloat(circle.attr('r')));

        this.target.select('svg').style('cursor', 'default');
        this.saveCircle(g);
      });
  }

  /**
   * STROKE FUNCTIONALITY
   *
   */

  stroke() {
    this.target.select('svg').on('.zoom', null).on('click', null);
    this.isStrokeSubmenuActive = !this.isStrokeSubmenuActive;

    if (this.isStrokeSubmenuActive) {
      this.selectedAction = 'stroke';
      this.target.select('svg').style('cursor', 'default');
    } else {
      this.selectedAction = null;
    }
  }

  selectStroke(stroke) {
    this.strokeWidth = stroke;

    if (this.selectedShapeElement) {
      this.target
        .select('#rect' + this.selectedShapeElement.attr('id').toString().split('-')[1])
        .style('stroke-width', this.strokeWidth);
      this.target
        .select('#circle-' + this.selectedShapeElement.attr('id').toString().split('-')[1])
        .style('stroke-width', this.strokeWidth);
    }

    if (this.selectedPath) {
      this.selectedPath.attr('stroke-width', this.strokeWidth);
    }

    this.selectedAction = null;
  }

  /**
   * COLOR FUNCTIONALITY
   *
   */

  color() {
    this.isColorSubmenuActive = !this.isColorSubmenuActive;

    if (this.isColorSubmenuActive) {
      this.selectedAction = 'color';
      // this.g.on('click', null);
      this.target.select('svg').style('cursor', 'default');
    } else {
      this.selectedAction = null;
    }
  }

  selectColor(color: any) {
    this.selectedColor = color;

    if (this.selectedNumberingElement) {
      this.target.select('#c' + this.selectedNumberingElement.attr('id')).attr('fill', this.selectedColor.background);

      this.target.select('#t' + this.selectedNumberingElement.attr('id')).attr('fill', this.selectedColor.foreground);

      this.saveNumbering(this.selectedNumberingElement);
    }

    if (this.selectedShapeElement) {
      this.target
        .select('#rect' + this.selectedShapeElement.attr('id').toString().split('-')[1])
        .attr('stroke', this.selectedColor.background);
      this.target
        .select('#circle-' + this.selectedShapeElement.attr('id').toString().split('-')[1])
        .attr('stroke', this.selectedColor.background);
      this.selectedShapeColor = color.background;
    }

    if (this.selectedPath) {
      this.selectedPath.attr('stroke', color.background);
      this.previousPathColor = color.background;

      this.savePath(this.selectedPath);
    }

    this.selectedAction = null;
  }

  /**
   * FUNCTIONALITY
   */

  removeEventsOnPreviousElements(isDelete?) {
    this.target.select('body').on('keyup', null);

    const modal = d3.select('.photoeditor-modal');
    modal.classed('show-modal', false);

    if (this.selectedNumberingElement) {
      this.removeNumberingEventsOnPreviousElement(isDelete);
    }
    if (this.selectedShapeElement) {
      this.removeShapeEventsOnPreviousElement();
    }
    if (this.selectedPath) {
      this.removePreviousPathEvents(isDelete);
    }

    this.target.select('svg').on('.zoom', null);
    this.zoomD3 = null;
    this.g
      .on('mousedown', null)
      .on('touchstart', null)
      .on('touchend', null)
      .on('touchleave', null)
      .on('mouseup', null)
      .on('mouseleave', null);
  }

  clearShape() {
    this.g.on('click', null);
    this.selectedAction = null;
    this.removeEventsOnPreviousElements();
  }

  deleteShape() {
    if (this.selectedNumberingElement) {
      this.removeFromOutputObject(this.selectedNumberingElement.attr('uid'));
      this.selectedNumberingElement.remove();
      this.reNumber();
    }

    if (this.selectedShapeElement) {
      this.removeFromOutputObject(this.selectedShapeElement.attr('uid'));
      this.selectedShapeElement.remove();
    }

    if (this.selectedPath) {
      this.removeFromOutputObject(this.selectedPath.attr('uid'));
      this.selectedPath.remove();
    }

    this.elementsCounter--;
    this.removeEventsOnPreviousElements(true);
    this.getOutput.emit(this.outputObject);
  }

  closeMenus() {
    this.isShapeSubmenuActive = false;
    this.isColorSubmenuActive = false;
  }

  /**
   * UTILS ---------------------------------------------------------------------------------------------
   */

  drawInputObjects() {
    if (!this.inputObject) {
      return false;
    }

    if (this.inputObject.backgroundImage) {
      this.imgSrc = this.inputObject.backgroundImage;
    }

    if (this.inputObject.backgroundImageUrl) {
      this.imgSrc = this.inputObject.backgroundImageUrl;
    }

    if (this.inputObject.size) {
      this.svg.attr('width', this.inputObject.size[0]).attr('height', this.inputObject.size[1]);
    }

    // if no annotation data is present, return from function
    if (this.inputObject.annotations && this.inputObject.annotations.items.length === 0) {
      return false;
    }

    // draw numbering
    this.drawNumberingElements();

    // draw freeHand
    this.drawFreeHandElements();

    // draw rectangle
    this.drawRectangle();

    // draw circle
    this.drawCircle();
  }

  /**
   * NUMBERING UTILS
   */

  saveNumbering(g) {
    const previousElementId = g.attr('id');
    const circle = this.target.select('#c' + previousElementId);

    const objectData = {
      position: [parseFloat(circle.attr('cx')), parseFloat(circle.attr('cy'))],
      color: circle.attr('fill'),
      strokeWidth: parseFloat(circle.style('stroke-width')),

      description: g.attr('title'),
      number: parseInt(previousElementId)
    };

    const uid = g.attr('uid');
    const numberingObject = {
      id: uid,
      type: ModelItemType.number,
      data: objectData
    };

    const isSaved = this.outputObject.annotations.items.findIndex((item) => item.id === uid);

    if (isSaved === -1) {
      this.outputObject.annotations.items.push(numberingObject);
    } else {
      this.outputObject.annotations.items[isSaved] = numberingObject;
    }

    this.outputObject.svg = this.svg;

    this.getOutput.emit(this.outputObject);
  }

  drawNumberingElements() {
    if (this.inputObject == null) {
      return;
    }

    const numbers = this.inputObject.annotations.items.filter((item) => item.type === ModelItemType.number);

    for (const number of numbers) {
      const coordinates = number.data.position;

      const newG = this.target
        .select('g')
        .append('g')
        .attr('id', this.numberingIndex)
        .attr('uid', number.id)
        .attr('x', coordinates[0])
        .attr('y', coordinates[1]);

      newG
        .append('circle')
        .attr('r', '2%')
        .attr('stroke', 'white')
        .style('stroke-width', 1)
        .attr('fill', number.data.color)
        .attr('cx', coordinates[0])
        .attr('cy', coordinates[1])
        .attr('id', 'c' + number.data.number);

      newG
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .attr('x', coordinates[0])
        .attr('y', coordinates[1])
        .attr('fill-opacity', 1)
        .attr('fill', 'black')
        .attr('id', 't' + number.data.number)
        .text(number.data.number.toString())
        .attr('style', 'user-select: none; font-size: 25px');

      newG.attr('title', number.data.description);

      this.saveNumbering(newG);

      newG
        .on('click', (event: any) => {
          if (!this.selectedAction || ['description', 'color', 'stroke'].indexOf(this.selectedAction) !== -1) {
            this.doActionsNumbering(newG);
          }
        })
        .on('touchend', () => {
          if (!this.selectedAction || ['description', 'color', 'stroke'].indexOf(this.selectedAction) !== -1) {
            this.doActionsNumbering(newG);
          }
        })
        .on('dblclick', () => {
          this.description();
        });

      this.g.on('click', null);
      this.target.select('svg').style('cursor', 'default');
      this.selectedAction = null;

      this.elementsCounter++;
      this.numberingIndex++;
      this.actionsCounter++;
    }

    this.isModalVisible = true;
  }

  /**
   * PATH UTILS
   */

  savePath(path) {
    const objectData = {
      position: [],
      color: path.attr('stroke'),
      strokeWidth: parseFloat(path.attr('stroke-width')),

      path: path.attr('d')
    };

    const uid = path.attr('uid');
    const pathObject = {
      id: uid,
      type: ModelItemType.freehand,
      data: objectData
    };

    const isSaved = this.outputObject.annotations.items.findIndex((item) => item.id === uid);

    if (isSaved === -1) {
      this.outputObject.annotations.items.push(pathObject);
    } else {
      this.outputObject.annotations.items[isSaved] = pathObject;
    }

    this.outputObject.svg = this.svg;
    this.getOutput.emit(this.outputObject);
  }

  drawFreeHandElements() {
    const paths = this.inputObject.annotations.items.filter((item) => item.type === ModelItemType.freehand);

    for (const path of paths) {
      const newPath = this.g
        .append('path') // start a new line
        .attr('stroke', path.data.color)
        .attr('uid', path.id)
        .attr('fill', 'none')
        .attr('stroke-width', path.data.strokeWidth)
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('d', path.data.path)
        .on('click', () => {
          if (this.selectedAction === null) {
            this.selectLine(newPath);
          }
        });

      this.savePath(newPath);
    }
  }

  /**
   * RECTANGLE UTILS
   */

  saveRectangle(g) {
    const previousElementId = g.attr('id');

    const rectangle = this.target.select('#rect' + previousElementId.split('-')[1]);

    const objectData = {
      position: [parseFloat(rectangle.attr('x')), parseFloat(rectangle.attr('y'))],
      color: rectangle.attr('stroke'),
      strokeWidth: parseFloat(rectangle.style('stroke-width')),

      size: [parseFloat(rectangle.attr('width')), parseFloat(rectangle.attr('height'))]
    };

    const uid = g.attr('uid');
    const rectangleObject = {
      id: uid,
      type: ModelItemType.rectangle,
      data: objectData
    };

    const isSaved = this.outputObject.annotations.items.findIndex((item) => item.id === uid);

    if (isSaved === -1) {
      this.outputObject.annotations.items.push(rectangleObject);
    } else {
      this.outputObject.annotations.items[isSaved] = rectangleObject;
    }

    this.outputObject.svg = this.svg;
    this.getOutput.emit(this.outputObject);
  }

  drawRectangle() {
    const rectangles = this.inputObject.annotations.items.filter((item) => item.type === ModelItemType.rectangle);

    for (const rect of rectangles) {
      const newG = this.g
        .append('g')
        .attr('x', rect.data.position[0])
        .attr('y', rect.data.position[1])
        .attr('id', 'g-' + this.actionsCounter)
        .attr('uid', rect.id);

      const rectangle = newG
        .append('rect')
        .attr('stroke', rect.data.color)
        .style('stroke-width', rect.data.strokeWidth)
        .attr('width', rect.data.size[0])
        .attr('height', rect.data.size[1])
        .attr('fill', 'none')
        .attr('id', 'rect' + this.actionsCounter);

      rectangle.attr('x', rect.data.position[0]).attr('y', rect.data.position[1]);

      newG
        .on('click', (event: any) => {
          if (!this.selectedAction || ['color', 'stroke'].indexOf(this.selectedAction) !== -1) {
            this.doActionsRectangle(newG, rectangle);
          }
        })
        .on('touchend', () => {
          if (!this.selectedAction || ['color', 'stroke'].indexOf(this.selectedAction) !== -1) {
            this.doActionsRectangle(newG, rectangle);
          }
        });

      this.saveRectangle(newG);

      this.elementsCounter++;
      this.actionsCounter++;
    }
  }

  /**
   * CIRCLE UTILS
   */

  saveCircle(g) {
    const previousElementId = g.attr('id');

    const circle = this.target.select('#circle-' + previousElementId.split('-')[1]);

    const objectData = {
      position: [parseFloat(circle.attr('cx')), parseFloat(circle.attr('cy'))],
      color: circle.attr('stroke'),
      strokeWidth: parseFloat(circle.style('stroke-width')),
      radius: circle.attr('r')
    };

    const uid = g.attr('uid');
    const rectangleObject = {
      id: uid,
      type: ModelItemType.circle,
      data: objectData
    };

    const isSaved = this.outputObject.annotations.items.findIndex((item) => item.id === uid);

    if (isSaved === -1) {
      this.outputObject.annotations.items.push(rectangleObject);
    } else {
      this.outputObject.annotations.items[isSaved] = rectangleObject;
    }

    this.outputObject.svg = this.svg;
    this.getOutput.emit(this.outputObject);
  }

  drawCircle() {
    const circles = this.inputObject.annotations.items.filter((item) => item.type === ModelItemType.circle);

    for (const circ of circles) {
      const newG = this.target
        .select('g')
        .append('g')
        .attr('id', 'g-' + this.actionsCounter)
        .attr('x', circ.data.position[0])
        .attr('y', circ.data.position[1])
        .attr('uid', circ.id);

      const circle = newG
        .append('circle')
        .attr('r', circ.data.radius)
        .attr('stroke', circ.data.color)
        .style('stroke-width', circ.data.strokeWidth)
        .attr('fill', 'none')
        .attr('id', 'circle-' + this.actionsCounter)
        .attr('cx', circ.data.position[0])
        .attr('cy', circ.data.position[1]);

      newG
        .on('click', (event: any) => {
          if (!this.selectedAction) {
            this.doActionsCircle(newG, circle);
          }
        })
        .on('touchend', () => {
          if (!this.selectedAction) {
            this.doActionsCircle(newG, circle);
          }
        });

      this.saveCircle(newG);

      this.elementsCounter++;
      this.actionsCounter++;
    }
  }

  /**
   * GENERAL UTILS
   */

  generateUID() {
    let newId;
    do {
      newId = this.makeid(this.uidLength);
    } while (this.generatedIds.includes(newId));

    this.generatedIds.push(newId);
    return newId;
  }

  makeid(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  removeFromOutputObject(uid: string) {
    const index = this.outputObject.annotations.items.findIndex((item) => item.id === uid);

    if (index !== -1) {
      this.outputObject.annotations.items.splice(index, 1);
    }
  }
}
